import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import {
  buildBranchListQueryString,
  normalizePro20PageResponse,
} from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  CreateTagPayload,
  DeleteTagParams,
  GetTagByIdParams,
  Tag,
  TagListParams,
  UpdateTagPayload,
} from '../types/tag.types';
import { normalizeTag } from '../utils/normalize-tag';

export const tagsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTagsByBranch: builder.query<PaginatedResponse<Tag>, TagListParams>({
      query: ({ branchCode, page, size, sortBy, sortDirection, search }) => ({
        url: `/tags/branch/${branchCode}${buildBranchListQueryString({
          page,
          size,
          sortBy,
          sortDirection,
          search,
        })}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          normalizeTag,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Tags, id: branchCode },
      ],
    }),
    getTagById: builder.query<Tag, GetTagByIdParams>({
      query: ({ branchTagId }) => ({
        url: `/tags/${String(branchTagId)}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeTag(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid tag response');
        }
        return normalized;
      },
      providesTags: (result) =>
        result
          ? [
              { type: API_TAGS.Tags, id: result.branchCode },
              { type: API_TAGS.Tags, id: String(result.branchTagId) },
            ]
          : [],
    }),
    createTag: builder.mutation<Tag, CreateTagPayload>({
      query: (body) => ({
        url: '/tags',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeTag(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid create tag response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Tags, id: branchCode },
      ],
    }),
    updateTag: builder.mutation<Tag, UpdateTagPayload>({
      query: ({ branchTagId, ...body }) => ({
        url: `/tags/${String(branchTagId)}`,
        method: 'PUT',
        body: { branchTagId, ...body },
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeTag(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid update tag response');
        }
        return normalized;
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: API_TAGS.Tags, id: result.branchCode },
              { type: API_TAGS.Tags, id: String(result.branchTagId) },
            ]
          : [],
    }),
    deleteTag: builder.mutation<null, DeleteTagParams>({
      query: ({ branchTagId }) => ({
        url: `/tags/${String(branchTagId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { branchCode, branchTagId }) => [
        { type: API_TAGS.Tags, id: branchCode },
        { type: API_TAGS.Tags, id: String(branchTagId) },
      ],
    }),
    duplicateTag: builder.mutation<Tag, DeleteTagParams>({
      query: ({ branchTagId }) => ({
        url: `/tags/duplicate/${String(branchTagId)}`,
        method: 'PUT',
      }),
      transformResponse: (response: unknown) => {
        const normalized = normalizeTag(normalizePro20Response(response));
        if (!normalized) {
          throw new Error('Invalid duplicate tag response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode, branchTagId }) => [
        { type: API_TAGS.Tags, id: branchCode },
        { type: API_TAGS.Tags, id: String(branchTagId) },
      ],
    }),
  }),
});

export const {
  useGetTagsByBranchQuery,
  useLazyGetTagsByBranchQuery,
  useGetTagByIdQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useDuplicateTagMutation,
} = tagsApi;
